import { memo, useEffect, useMemo, useState } from "react";
import {
  Button,
  Dimmer,
  Form,
  FormField,
  FormGroup,
  Loader,
  Modal,
} from "semantic-ui-react";

// Serializers
import BroadcastPostSerializer from "issara-sdk/types/BroadcastPostSerializer_apps_PRX";
import BroadcastPostActionLogSerializer from "issara-sdk/types/BroadcastPostActionLogSerializer_apps_PRX";

// Utils
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import { Table } from "react-lib/frameworks/Table";
import { getPostById } from "./MeBroadcastUtils";

// Types
import { BroadcastDataFirebase, COLLECTION } from "./MeFormType";

// Type
type ModBroadcastLogProps = {
  // func & callback
  open: boolean;
  onClose: () => void;
  // data
  selectedBroadcast: BroadcastPostSerializer;
  broadcastLog: BroadcastPostActionLogSerializer[];
  // loader
  loader?: boolean;
};

type RenderBroadcastPreviewProps = {
  broadcastFirebase: string;
};

const RenderBroadcastPreview = (props: RenderBroadcastPreviewProps) => {
  const { broadcastFirebase } = props;

  const [firebaseData, setFirebaseData] = useState<BroadcastDataFirebase | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      if (!broadcastFirebase) return;

      setLoading(true);
      const result = await getPostById(COLLECTION, broadcastFirebase);
      setLoading(false);

      if (result) setFirebaseData(result);
    };

    load();
  }, [broadcastFirebase]);

  const isPictureExist =
    firebaseData?.previews_picture && firebaseData.previews_picture?.length > 0;

  return (
    <div
      style={{ position: "relative", width: "100%", border: "solid 1px black", padding: "1rem" }}
    >
      {/* --- Loader --- */}
      <Dimmer active={loading} inverted>
        <Loader />
      </Dimmer>

      {!firebaseData ? (
        <div>No Preview</div>
      ) : (
        <div style={{ display: "flex", alignItems: "center" }}>
          {isPictureExist && (
            <div>
              <img
                key={firebaseData.previews_picture?.[0]}
                src={firebaseData.previews_picture?.[0]}
                alt="preview"
                style={{ width: "80px", height: "80px", marginRight: "1rem" }}
              />
            </div>
          )}
          {firebaseData.previews_text ? (
            <div>
              <label>
                {firebaseData.previews_text.slice(0, isPictureExist ? 25 : 35).concat("...")}
              </label>
            </div>
          ) : (
            <div>No Preview</div>
          )}
        </div>
      )}
    </div>
  );
};

const ModBroadcastLog = (props: ModBroadcastLogProps) => {
  console.log("Yeti [ModLog] Props: ", props);

  const formattedLogList = useMemo(() => {
    return (props.broadcastLog || []).map((item) => ({
      ...item,
      datetime: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {item?.datetime ? formatISOtoBEDateTime(item?.datetime).dateTimeShortBracket : ""}
        </div>
      ),
      action_custom_label: (
        <div style={{ display: "flex", justifyContent: "center" }}>{item?.action_custom_label}</div>
      ),
      after_action_status_custom_label: (
        <div style={{ display: "flex", justifyContent: "center" }}>
          {item?.after_action_status_custom_label}
        </div>
      ),
      user_name: <div style={{ display: "flex", justifyContent: "center" }}>{item?.user_name}</div>,
    }));
  }, [props.broadcastLog]);

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <Form style={{ margin: "2rem" }}>
        <FormGroup inline>
          <FormField width={5}>
            <div style={{ width: "100%" }}>
              <label>
                <span style={{ fontWeight: "bold" }}>Broadcast ID: </span>
                {props.selectedBroadcast?.broadcast_id}
              </label>
            </div>
          </FormField>

          <FormField width={6}>
            <div style={{ width: "100%" }}>
              <RenderBroadcastPreview broadcastFirebase={props.selectedBroadcast?.firebase_id} />
            </div>
          </FormField>

          <FormField width={5}>
            <div style={{ width: "100%" }}>
              <label>
                <span style={{ fontWeight: "bold" }}>Broadcast Date: </span>
                {props.selectedBroadcast?.broadcasted_at
                  ? formatISOtoBEDateTime(props.selectedBroadcast?.broadcasted_at)
                      .dateTimeShortBracket
                  : "-"}
              </label>
            </div>
          </FormField>
        </FormGroup>

        <FormGroup inline>
          <FormField>
            <div style={{ width: "100%" }}>
              <span style={{ fontWeight: "bold" }}>Group Name: </span>
              {props.selectedBroadcast?.group_name || ""}
            </div>
          </FormField>
        </FormGroup>

        <FormGroup inline>
          <FormField width={16} style={{ padding: 0 }}>
            <div style={{ position: "relative", width: "100%" }}>
              <Dimmer active={props.loader} inverted>
                <Loader />
              </Dimmer>
              <Table
                headers="Datetime, Action, Status, User"
                keys="datetime, action_custom_label, after_action_status_custom_label, user_name"
                showPagination={false}
                minRows={5}
                style={{ overflowY: "auto", height: "200px" }}
                data={formattedLogList || []}
              />
            </div>
          </FormField>
        </FormGroup>

        <FormGroup inline style={{ display: "flex", justifyContent: "center" }}>
          <FormField style={{ padding: 0 }}>
            <div style={{ width: "100%" }}>
              <Button fluid color="grey" children="Close" onClick={props.onClose} />
            </div>
          </FormField>
        </FormGroup>
      </Form>
    </Modal>
  );
};

ModBroadcastLog.displayName = "ModBroadcastLog";

export default memo(ModBroadcastLog);
